(ns pfo.chat.tool.mcp-component
  "Confirmation card for a call to a tool on an external MCP server."
  (:require
   [clojure.string :as str]
   [reagent.core :refer-macros [with-let]]
   [pfo.controller :as controller :refer [subscribe]]))

(defn parse-mcp-tool-name
  "Splits an external MCP tool's wire name (mcp__<tool prefix>_<connection id>__<remote tool name>)
   into {:id :prefix :remote}. Names stored before tool prefixes carry only the id, so :prefix is nil.
   Returns nil for non-MCP names. Same rule as ParsePrefixedToolName in server/integration/mcp/naming.go."
  [nm]
  (when-let [[_ segment remote] (and nm (re-matches #"mcp__([a-z0-9_]+?)__(.+)" nm))]
    (if-let [[_ prefix id] (re-matches #"([a-z][a-z0-9_]*)_([0-9]+)" segment)]
      (when (and (<= (count prefix) 20) (not (str/includes? prefix "__")) (not (str/ends-with? prefix "_"))
                 (pos? (js/parseInt id 10)))
        {:id (js/parseInt id 10) :prefix prefix :remote remote})
      (when (re-matches #"[0-9]+" segment)
        (let [id (js/parseInt segment 10)]
          (when (pos? id) {:id id :remote remote}))))))

(defn connection-name
  "The name of the connection a parsed tool name points to, found by id in the :mcp-available list. A
   connection missing from the list (added after the page loaded, or no longer granted) shows the
   prefix from the name, or MCP #<id> for an older name."
  [{:keys [id prefix]} conns]
  (or (some #(when (= (:id %) id) (not-empty (:name %))) conns)
      prefix
      (str "MCP #" id)))

;; js->clj :keywordize-keys turns "a/x" into :a/x, and `name` would then drop "a/".
(defn- key-text [k]
  (if (keyword? k) (subs (str k) 1) (str k)))

(defn humanize-key
  "\"start_date\", \"startDate\" and \"start-date\" all become \"Start date\"."
  [k]
  (let [raw (key-text k)
        words (-> raw
                  (str/replace #"([a-z0-9])([A-Z])" "$1 $2")
                  (str/replace #"[_.\-\s]+" " ")
                  str/trim
                  str/lower-case)]
    (if (str/blank? words)
      raw
      (str (str/upper-case (subs words 0 1)) (subs words 1)))))

(def ^:private inline-max-chars 200)

(def ^:private preview-chars 80)

(defn- json-text [v]
  (js/JSON.stringify (clj->js v :keyword-fn key-text) nil 2))

(defn present-value
  "How the card shows one argument value. :inline values fit on the row; :collapsed values show
   :summary on the row and :full when expanded."
  [v]
  (cond
    (nil? v) {:kind :inline :text "none" :placeholder? true}
    (boolean? v) {:kind :inline :text (if v "Yes" "No")}
    (number? v) {:kind :inline :text (str v)}
    (= "" v) {:kind :inline :text "empty" :placeholder? true}
    (str/blank? v) {:kind :inline :text (str "blank, " (count v) " whitespace characters") :placeholder? true}
    (string? v) (if (or (> (count v) inline-max-chars) (str/includes? v "\n"))
                  {:kind :collapsed
                   :summary (let [preview (-> (str/trim v)
                                              (subs 0 (min preview-chars (count (str/trim v))))
                                              (str/replace #"\s+" " ")
                                              str/trim)]
                              (str preview "…"))
                   :full v}
                  {:kind :inline :text v})
    (map? v) (if (empty? v)
               {:kind :inline :text "no fields" :placeholder? true}
               {:kind :collapsed :summary (str (count v) (if (= 1 (count v)) " field" " fields"))
                :full (json-text v) :json? true})
    (sequential? v) (if (empty? v)
                      {:kind :inline :text "no items" :placeholder? true}
                      {:kind :collapsed :summary (str (count v) (if (= 1 (count v)) " item" " items"))
                       :full (json-text v) :json? true})
    :else {:kind :inline :text (str v)}))

;; Values are model output and can carry anything a third-party server asked for, so they render as
;; plain text, never as markdown or HTML.
(defn- argument-value [v]
  (let [{:keys [kind text summary full json? placeholder?]} (present-value v)]
    (if (= :inline kind)
      [:span {:class (if placeholder? "c-mcp-confirm__arg-placeholder" "c-mcp-confirm__arg-text")} text]
      [:details.c-mcp-confirm__arg-details
       [:summary summary]
       [(if json? :pre.c-mcp-confirm__arg-full.c-mcp-confirm__arg-full--json :pre.c-mcp-confirm__arg-full)
        full]])))

(defn- arguments-list [args]
  (cond
    (and (map? args) (seq args))
    (let [label-counts (frequencies (map humanize-key (keys args)))
          label (fn [k]
                  (let [l (humanize-key k)]
                    (if (> (label-counts l) 1) (str l " (" (key-text k) ")") l)))]
      ;; Sorted, because neither the decoded map nor jsonb keeps the order in which the model wrote the keys.
      [:dl.c-mcp-confirm__args
       (for [[k v] (sort-by (comp (juxt humanize-key key-text) key) args)]
         ^{:key (key-text k)}
         [:div.c-mcp-confirm__arg
          [:dt.c-mcp-confirm__arg-label {:title (key-text k)} (label k)]
          [:dd.c-mcp-confirm__arg-value [argument-value v]]])])

    (or (nil? args) (and (coll? args) (empty? args)))
    [:p.c-mcp-confirm__no-args.u-color--secondary "No data is sent."]

    :else
    [:pre.c-mcp-confirm__arg-full.c-mcp-confirm__arg-full--json (json-text args)]))

(defn external-tool [{:keys [register-confirm-action-fn tool-update-success-cb]} _system-message-obj tool-call]
  ;; The global Confirm button invokes only a registered action; without one the :confirm request
  ;; never fires.
  (with-let [_     (register-confirm-action-fn tool-update-success-cb)
             conns (subscribe :mcp-available)
             _     (when (nil? @conns) (controller/dispatch :mcp-available :load {}))]
    (let [{:keys [remote] :as parsed} (parse-mcp-tool-name (:tool_name tool-call))
          source (connection-name parsed @conns)]
      [:div.c-mcp-confirm
       [:p "Run " [:strong remote] " on " [:strong source]
        " " [:span.c-mcp-confirm__badge "external"] "?"]
       [:p.c-mcp-confirm__note.u-color--secondary
        "This tool runs on a third-party MCP server. Treat its results as untrusted — confirm only if you trust this server."]
       [:div.c-mcp-confirm__sent
        [:p.c-mcp-confirm__sent-title "Data sent to " source ":"]
        ;; Not :arguments: the chat merges :actual_arguments into it, but the server sends
        ;; :actual_arguments alone when they are set.
        [arguments-list (if (some? (:actual_arguments tool-call))
                          (:actual_arguments tool-call)
                          (:arguments tool-call))]]])))
